// Pieces shared by the Outbound and Inbound analytics pages: the segmented
// period control (with the custom from/to month-range popover) and the CSV
// download helper.
import { useRef, useState } from "react"
import { Box, Button, Popover, Typography } from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import FileSaver from "file-saver"
import { SegmentedPeriod } from "components/layout/segmented-period"
import { trackEvent } from "utils/analytics"

const C = { text: "#12131A", brand: "#F3DA00" }

const SEG_OPTIONS = [
  { key: "3m", label: "3M" },
  { key: "6m", label: "6M" },
  { key: "12m", label: "12M" },
  { key: "24m", label: "24M" },
  { key: "custom", label: "Custom" },
]

const ymOf = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`
const toYmDate = ym => {
  if (!/^\d{4}-\d{2}$/.test(String(ym || ""))) return null
  const [year, month] = ym.split("-").map(Number)
  if (!Number.isFinite(year) || !Number.isFinite(month)) return null
  return new Date(year, month - 1, 1)
}

// `period` is a PERIODS key string or { custom: true, fromYM, toYM } — the same
// value deriveVolumeView accepts. onChange receives the next period value.
export const PeriodFilter = ({ period, onChange, minYear }) => {
  const [open, setOpen] = useState(false)
  const [fromDate, setFromDate] = useState(null)
  const [toDate, setToDate] = useState(null)
  const anchorRef = useRef(null)

  const segValue = typeof period === "string" ? period : "custom"
  const minDate = Number.isFinite(minYear) ? new Date(minYear, 0, 1) : null

  const apply = () => {
    if (!fromDate || !toDate) return
    const safeFrom = minDate && fromDate < minDate ? minDate : fromDate
    const safeTo = minDate && toDate < minDate ? minDate : toDate
    let a = ymOf(safeFrom)
    let b = ymOf(safeTo)
    if (a > b) [a, b] = [b, a]
    onChange({ custom: true, fromYM: a, toYM: b })
    setOpen(false)
  }

  const onFromChange = next => {
    if (!next) {
      setFromDate(null)
      return
    }
    setFromDate(minDate && next < minDate ? minDate : next)
  }

  const onToChange = next => {
    if (!next) {
      setToDate(null)
      return
    }
    setToDate(minDate && next < minDate ? minDate : next)
  }

  const openCustom = () => {
    if (period?.custom && period.fromYM && period.toYM) {
      const from = toYmDate(period.fromYM)
      const to = toYmDate(period.toYM)
      setFromDate(from && minDate && from < minDate ? minDate : from)
      setToDate(to && minDate && to < minDate ? minDate : to)
    }
    setOpen(true)
  }

  return (
    <>
      <Box ref={anchorRef}>
        <SegmentedPeriod value={segValue} onChange={k => (k === "custom" ? openCustom() : onChange(k))} options={SEG_OPTIONS} />
      </Box>
      <Popover open={open} anchorEl={anchorRef.current} onClose={() => setOpen(false)} anchorOrigin={{ vertical: "bottom", horizontal: "left" }}>
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5, width: 240 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.text }}>Custom date range</Typography>
          <DatePicker label="From" views={["year", "month"]} value={fromDate} onChange={onFromChange} minDate={minDate || undefined} slotProps={{ textField: { size: "small" } }} />
          <DatePicker label="To" views={["year", "month"]} value={toDate} onChange={onToChange} minDate={minDate || undefined} slotProps={{ textField: { size: "small" } }} />
          <Button
            variant="contained"
            size="small"
            disableElevation
            onClick={apply}
            disabled={!fromDate || !toDate}
            sx={{ bgcolor: C.brand, color: C.text, fontWeight: 700, "&:hover": { bgcolor: "#dcc500" } }}
          >
            Apply
          </Button>
        </Box>
      </Popover>
    </>
  )
}

export const downloadCsv = (filename, headers, rows, entity) => {
  const escape = v => {
    const s = v === null || v === undefined ? "" : String(v)
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  const lines = [headers.map(h => escape(h.label)).join(",")]
  rows.forEach(r => lines.push(headers.map(h => escape(r[h.key])).join(",")))
  FileSaver.saveAs(new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" }), filename)
  trackEvent("export", { entity, format: "csv", columns_count: headers.length })
}
